import type { ReactNode } from "react";
import { site } from "../../config/site";
import { linkProps } from "../lib/router";

export function Monogram({ className = "" }: { className?: string }) {
  return (
    <a
      {...linkProps("/")}
      className={`font-display text-lg tracking-widest text-ink no-underline ${className}`}
    >
      {site.partnerOne[0]} <span className="text-muted">&</span> {site.partnerTwo[0]}
    </a>
  );
}

export function Header({ right }: { right?: ReactNode }) {
  return (
    <header className="flex items-center justify-between px-6 py-6 sm:px-10">
      <Monogram />
      <div className="flex items-center gap-3">{right}</div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-line px-6 py-10 text-center sm:px-10">
      <p className="font-display text-2xl">
        {site.partnerOne} &amp; {site.partnerTwo}
      </p>
      <p className="eyebrow mt-3">
        {site.dateLabel} · {site.locationLabel}
      </p>
      <p className="mt-6 text-sm text-muted">
        Questions? Write to{" "}
        <a className="underline decoration-line underline-offset-4" href={`mailto:${site.contactEmail}`}>
          {site.contactEmail}
        </a>
      </p>
    </footer>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto min-h-screen max-w-5xl">{children}</div>;
}

/** Centered single-column shell used by the auth and onboarding screens. */
export function NarrowPage({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto min-h-screen max-w-lg px-6">
      <Header />
      <main className="animate-rise pt-8 pb-16">{children}</main>
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-20 text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-sage" />
      <span className="text-sm">{label}…</span>
    </div>
  );
}

export function Notice({
  tone = "info",
  children,
}: {
  tone?: "info" | "error" | "success";
  children: ReactNode;
}) {
  const tones = {
    info: "bg-sage-soft text-ink",
    error: "bg-[#fbeeea] text-clay",
    success: "bg-sage-soft text-sage",
  } as const;
  return <div className={`rounded-xl px-4 py-3 text-sm ${tones[tone]}`}>{children}</div>;
}
